"use client"

import * as Sentry from "@sentry/nextjs"
import { useEffect, useState } from "react"
import { isVersionSkewError } from "@/lib/deploy/versionSkew"
import { nativeSafeReload } from "@/lib/capacitor/nav"

// C-20 (audit 2026-05-27): previously rendered <NextError statusCode={0} />
// — the bare Vercel-style English fallback with no reset button. Crashes
// on a POS terminal left staff with no recovery short of pulling the
// power. This page now renders a DS-styled error card with a "Recargar"
// button and a copyable digest so the operator can text the manager.
//
// Per Next.js 16, global-error.tsx must render its own <html>/<body>
// because it replaces the root layout when the layout itself errors.
// We inline the redy CSS tokens so the page doesn't depend on
// globals.css having loaded successfully — the failure case might be
// that very stylesheet.

const REDY_TOKENS = `
  :root {
    --redy-charcoal: #1A1A1A;
    --redy-warm-white: #F7F5F1;
    --redy-secondary: #6B6762;
    --redy-muted: #9B9690;
    --redy-border: #EAE7E1;
    --redy-red: #D7263D;
    --redy-orange: #F4722B;
    --redy-gradient: linear-gradient(135deg, #D7263D 0%, #F4722B 100%);
  }
  body {
    margin: 0;
    font-family: 'Satoshi', 'Inter', system-ui, -apple-system, sans-serif;
    background: var(--redy-warm-white);
    color: var(--redy-charcoal);
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }
  .card {
    max-width: 480px;
    width: 100%;
    text-align: center;
  }
  .eyebrow {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--redy-red);
  }
  h1 {
    margin: 12px 0 0;
    font-size: 36px;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -0.02em;
  }
  h1 .grad {
    background: var(--redy-gradient);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
  p.lede {
    margin: 16px 0 0;
    font-size: 15px;
    color: var(--redy-secondary);
  }
  .digest {
    margin-top: 24px;
    display: inline-block;
    padding: 8px 12px;
    border-radius: 8px;
    background: rgba(0,0,0,0.04);
    color: var(--redy-muted);
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, monospace;
    cursor: pointer;
    border: 0;
  }
  .actions {
    margin-top: 28px;
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
  }
  button.primary {
    background: var(--redy-gradient);
    color: #fff;
    border: 0;
    border-radius: 16px;
    padding: 14px 28px;
    font-size: 15px;
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(215,38,61,0.22);
  }
  a.secondary {
    background: #fff;
    color: var(--redy-charcoal);
    border: 1px solid var(--redy-border);
    border-radius: 16px;
    padding: 14px 28px;
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    display: inline-block;
  }
`

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string }
}) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    Sentry.captureException(error, { tags: { zone: "global" } })
  }, [error])

  function copyDigest() {
    if (!error.digest) return
    void navigator.clipboard?.writeText(error.digest).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  function reload() {
    // nativeSafeReload: en el shell iOS un reload de documento completo fuera
    // del prefijo /tablet se entrega a Safari, así que el botón de la tarjeta
    // de error no hacía nada dentro de la app. Ver lib/capacitor/nav.ts.
    nativeSafeReload()
  }

  if (isVersionSkewError(error)) {
    return (
      <html lang="es">
        <head>
          <title>Actualización — Redy</title>
          <style dangerouslySetInnerHTML={{ __html: REDY_TOKENS }} />
        </head>
        <body>
          <div className="card">
            <p className="eyebrow">Nueva versión</p>
            <h1>
              Hay una versión <span className="grad">nueva</span>.
            </h1>
            <p className="lede">
              Toca actualizar para cargar la versión más reciente. Tu orden en
              curso está guardada.
            </p>
            <div className="actions">
              <button type="button" className="primary" onClick={reload}>
                Actualizar
              </button>
            </div>
          </div>
        </body>
      </html>
    )
  }

  return (
    <html lang="es">
      <head>
        <title>Error — Redy</title>
        <style dangerouslySetInnerHTML={{ __html: REDY_TOKENS }} />
      </head>
      <body>
        <div className="card">
          <p className="eyebrow">Algo se rompió</p>
          <h1>
            Tuvimos un <span className="grad">problema</span>.
          </h1>
          <p className="lede">
            Recarga la página para continuar. Si vuelve a pasar, envía el código de abajo a soporte.
          </p>
          {error.digest && (
            <button type="button" className="digest" onClick={copyDigest}>
              {copied ? "✓ Código copiado" : `Código: ${error.digest} · tap para copiar`}
            </button>
          )}
          <div className="actions">
            <button type="button" className="primary" onClick={reload}>
              Recargar
            </button>
            <a className="secondary" href="/home">
              Ir al inicio
            </a>
          </div>
        </div>
      </body>
    </html>
  )
}
